import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import APIClient from "../api/api";
import { DatePicker, Popover, Select, Skeleton, notification } from "antd";
import { FilterPill } from "../components/ui";
import { PageBody, PageHead } from "../layout";
import { DashboardStats, ListingActivity, PortfolioRenewalStats, PortfolioStats } from "../api/models";
import { ParkingRevenueSummary } from "../components/ParkingRevenueSummary";
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Legend, Tooltip as RechartsTooltip } from "recharts";
import moment from "moment-timezone";
import GrowthStatistic from "../components/GrowthStatistic";
import { formatCurrency, formatCurrencyCents } from "../utils";
import RenewalStats from "../components/RenewalStats";
import RenewalMethodStats from "../components/RenewalMethodStats";
import RevenueRuleBreakdown from "../components/RevenueRuleBreakdown";
import { SalesRevenueSummary } from "../components/SalesRevenueSummary";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import MetricsStats from "../components/MetricsStats";
import { AuditEventsModal } from "../components/AuditEventsModal";
import { defaultQuickRanges, useDateRangeFilter } from "../components/DateRangeFilter";

dayjs.extend(utc);


const chartColors = [
    '#7c7af0', '#3b82f6', '#f59e0b', '#06b6d4', '#ec4899', '#10b981', '#8b5cf6',
];

function DashboardCard({ title, children, className }: { title: React.ReactNode; children: React.ReactNode; className?: string }) {
    return (
        <div className={`bg-panel rounded-lg border border-border overflow-hidden flex flex-col ${className ?? ''}`}>
            <div className="px-3.5 py-2.5 border-b border-border flex items-center gap-2 min-h-[38px]">
                {typeof title === 'string' ? (
                    <h3 className="text-base font-semibold text-text m-0">{title}</h3>
                ) : title}
            </div>
            <div className="p-3 flex-1 min-h-0">{children}</div>
        </div>
    );
}

function ChartTooltipContent({ active, payload, label, formatter }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-panel border border-border rounded-md px-3 py-2 shadow-lg">
            <div className="text-2xs text-text-3 font-mono mb-1.5">{label}</div>
            {payload.map((p: any, i: number) => (
                <div key={p.dataKey + '-' + i} className="flex items-center gap-2 text-sm py-0.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
                    <span className="text-text-3 text-xs">{p.name || p.dataKey}</span>
                    <span className="text-text font-mono font-medium ml-auto tabular-nums">
                        {formatter ? formatter(p.value) : (typeof p.value === 'number' ? p.value.toLocaleString() : p.value)}
                    </span>
                </div>
            ))}
        </div>
    );
}

const axisTickStyle = { fontSize: 10, fill: 'rgb(var(--color-text-3))' };
const gridStroke = 'rgb(var(--color-border))';
const legendStyle = { fontSize: 11, color: 'rgb(var(--color-text-2))' };

function newPortfolioRenewalStats(): PortfolioRenewalStats {
    return {
        manual: 0,
        autoPicks: 0,
        legacyPicks: 0,
        other: 0,
        autoRenew: 0,
        methodAutoRenew: 0,
        methodSupportEmail: 0,
        methodLegacy: 0,
        methodOther: 0,
        total: 0,
    };
}

function newPortfolioStats(): PortfolioStats {
    return {
        totalDomains: 0,
        deletedTotal: 0,
        domainsAdded: 0,
        additions: newPortfolioRenewalStats(),
        existing: newPortfolioRenewalStats(),
    };
}

function addStats(existing: PortfolioStats, other: PortfolioStats) {
    existing.deletedTotal += other.deletedTotal;
    existing.domainsAdded += other.domainsAdded;
    existing.totalDomains += other.totalDomains;
    addRenewalStats(existing.additions, other.additions);
    addRenewalStats(existing.existing, other.existing);
}

function addRenewalStats(existing: PortfolioRenewalStats, other: PortfolioRenewalStats) {
    existing.autoPicks += other.autoPicks;
    existing.autoRenew += other.autoRenew;
    existing.legacyPicks += other.legacyPicks;
    existing.manual += other.manual;
    existing.methodAutoRenew += other.methodAutoRenew;
    existing.methodLegacy += other.methodLegacy;
    existing.methodOther += other.methodOther;
    existing.methodSupportEmail += other.methodSupportEmail;
    existing.other += other.other;
    existing.total += other.total;
}

function statsDivide(stats: PortfolioStats, n: number) {
    stats.deletedTotal /= n;
    stats.domainsAdded /= n;
    renewalStatsDivide(stats.additions, n);
    renewalStatsDivide(stats.existing, n);
}

function renewalStatsDivide(stats: PortfolioRenewalStats, n: number) {
    stats.autoPicks /= n;
    stats.autoRenew /= n;
    stats.legacyPicks /= n;
    stats.manual /= n;
    stats.other /= n;
    stats.total /= n;
    stats.methodAutoRenew /= n;
    stats.methodLegacy /= n;
    stats.methodOther /= n;
    stats.methodSupportEmail /= n;
}

interface Stats {
    currentPeriod: DashboardStats,
    previousPeriod: DashboardStats
}

const Home = () => {
    const [stats, setStats] = useState<DashboardStats>();
    const [notify, contextHolder] = notification.useNotification();

    const [periodStats, setPeriodStats] = useState<Stats>();
    const [selectedAccountId, setSelectedAccountId] = useState<string>('paritysoftware');

    const [auditModalVisible, setAuditModalVisible] = useState(false);
    const [auditModalConfig, setAuditModalConfig] = useState<{
        eventTypes: string[];
        title: string;
    } | null>(null);
    const [topSalesTab, setTopSalesTab] = useState<'normal' | 'lto'>('normal');

    async function loadStats() {
        try {
            setStats(undefined);
            setPeriodStats(undefined);

            const [response, responsePrevious] = await Promise.all([
                APIClient.dashboardStats(dateRange[0].toDate(), dateRange[1].toDate()),
                APIClient.dashboardStats(previousPeriodRange[0].toDate(), previousPeriodRange[1].toDate()),
            ]);

            setStats(response);
            setPeriodStats({
                currentPeriod: response,
                previousPeriod: responsePrevious,
            });
        }
        catch (e) {
            console.log(e);
            notify.error({ message: 'Failed to load at this time.' });
        }
    }

    function getDomainStatsDailyAvgPeriod(stats: DashboardStats): PortfolioStats {
        var currentAmount = newPortfolioStats();
        var currentTotal = 0;
        for (const entry of stats.portfolioStatsPeriod) {
            addStats(currentAmount, entry.stats);
            currentTotal += 1;
        }

        statsDivide(currentAmount, currentTotal);

        return currentAmount;
    }

    function getDomainsStats(ts: moment.Moment) {
        if (stats?.portfolioStatsDaily == null) return undefined;
        for (const entry of stats.portfolioStatsDaily) {
            const date = moment(entry.date).utc();
            if (date.year() == ts.year() && date.month() == ts.month() && date.date() == ts.date()) {
                return entry.stats;
            }
        }
        return undefined;
    }

    function getDomainStatsMTD() {
        if (stats?.portfolioStatsDaily == null) return undefined;

        const ts = moment();

        var mtdAmount = newPortfolioStats();
        for (const entry of stats.portfolioStatsDaily) {
            const date = moment(entry.date).utc();
            if (date.year() == ts.year() && date.month() == ts.month()) {
                addStats(mtdAmount, entry.stats);
            }
        }
        return mtdAmount;
    }

    function getCurrentDayLocal() {
        const now = moment().startOf('day');
        const todayUtc = moment(new Date(now.year(), now.month(), now.date()));
        return todayUtc;
    }

    function isPreviousMonth(date: Date) {
        const lastMonth = moment().utc().startOf('month').subtract(1, 'days');
        return moment(date).utc().year() == lastMonth.year() &&
            moment(date).utc().month() == lastMonth.month();
    }

    function isPreviousMonthSameDayOfMonth(date: Date) {
        const lastMonth = moment().utc().startOf('month').subtract(1, 'days');
        return moment(date).utc().year() == lastMonth.year() &&
            moment(date).utc().month() == lastMonth.month() &&
            moment(date).utc().date() <= moment().utc().date();
    }

    function isThisMonth(date: Date) {
        return moment(date).utc().year() == moment().utc().year() &&
            moment(date).utc().month() == moment().utc().month();
    }

    const salesRevenuePreviousPeriod = periodStats?.previousPeriod?.listingsStatsFull.reduce((acc, val) => {
        return acc + val.stats.reduce((acc, val) => acc + val.revenueUSD, 0.0);
    }, 0.0);

    const salesRecurringRevenueCurrentPeriod = periodStats?.currentPeriod?.listingsStatsFull.reduce((acc, val) => {
        return acc + val.stats.reduce((acc, val) => acc + val.recurringRevenueUSD, 0.0);
    }, 0.0);

    const salesRecurringRevenuePreviousPeriod = periodStats?.previousPeriod?.listingsStatsFull.reduce((acc, val) => {
        return acc + val.stats.reduce((acc, val) => acc + val.recurringRevenueUSD, 0.0);
    }, 0.0);

    const salesRevenueCurrentPeriod = periodStats?.currentPeriod?.listingsStatsFull.reduce((acc, val) => {
        return acc + val.stats.reduce((acc, val) => acc + val.revenueUSD, 0.0);
    }, 0.0);

    const salesCurrentPeriod = periodStats?.currentPeriod?.listingsStatsFull
        .reduce((acc, val) => {
            return acc + val.stats.reduce((acc, val) => acc + val.numSales, 0);
        }, 0);

    const salesPreviousPeriod = periodStats?.previousPeriod?.listingsStatsFull.reduce((acc, val) => {
            return acc + val.stats.reduce((acc, val) => acc + val.numSales, 0);
        }, 0);

    const ncAftermarketAuctionRevenueThisPeriod = periodStats?.currentPeriod?.ncAftermarketAuctions.reduce((acc, val) => {
        return acc + val.totalRevenueUSD;
    }, 0.0);

    const ncAftermarketAuctionRevenueLastPeriod = periodStats?.previousPeriod?.ncAftermarketAuctions.reduce((acc, val) => {
        return acc + val.totalRevenueUSD;
    }, 0.0);

    const ncAftermarketAuctionRevenuePortfolioThisPeriod = periodStats?.currentPeriod?.ncAftermarketAuctions.reduce((acc, val) => {
        return acc + val.totalRevenueUSDPortfolio;
    }, 0.0);

    const ncAftermarketAuctionRevenuePortfolioPreviousPeriod = periodStats?.previousPeriod?.ncAftermarketAuctions.reduce((acc, val) => {
        return acc + val.totalRevenueUSDPortfolio;
    }, 0.0);

    const autorenewDisabledCurrentPeriod = periodStats?.currentPeriod?.autorenewStats?.stats?.[selectedAccountId]?.autorenewDisabled;
    const autorenewDisabledPreviousPeriod = periodStats?.previousPeriod?.autorenewStats?.stats?.[selectedAccountId]?.autorenewDisabled;

    // Expiring this month stats - get first (start of month) and last (current) entries
    const expiringStatsCurrentPeriod = periodStats?.currentPeriod?.expiringThisMonthStats?.stats?.[selectedAccountId];
    const expiringStatsPreviousPeriod = periodStats?.previousPeriod?.expiringThisMonthStats?.stats?.[selectedAccountId];

    // Start of Month stats (first entry)
    const expiringStartOfMonthCurrent = expiringStatsCurrentPeriod?.[0];
    const expiringStartOfMonthPrevious = expiringStatsPreviousPeriod?.[0];

    // Current stats (last entry)
    const expiringCurrentCurrent = expiringStatsCurrentPeriod?.[expiringStatsCurrentPeriod.length - 1];
    const expiringCurrentPrevious = expiringStatsPreviousPeriod?.[expiringStatsPreviousPeriod.length - 1];

    // Calculate percentages for Start of Month
    // Return 0 if we have data but total is 0 (meaning 0 domains expiring = 0%)
    // Return undefined if we don't have data at all
    const expiringAutorenewOffPercentageStartCurrent = expiringStartOfMonthCurrent
        ? expiringStartOfMonthCurrent.total > 0
            ? (expiringStartOfMonthCurrent.autorenewDisabled || 0) / expiringStartOfMonthCurrent.total * 100
            : 0  // We have data, but 0 domains expiring = 0%
        : undefined;  // No data at all
    const expiringAutorenewOffPercentageStartPrevious = expiringStartOfMonthPrevious
        ? expiringStartOfMonthPrevious.total > 0
            ? (expiringStartOfMonthPrevious.autorenewDisabled || 0) / expiringStartOfMonthPrevious.total * 100
            : 0
        : undefined;

    // Calculate percentages for Current
    const expiringAutorenewOffPercentageCurrentCurrent = expiringCurrentCurrent
        ? expiringCurrentCurrent.total > 0
            ? (expiringCurrentCurrent.autorenewDisabled || 0) / expiringCurrentCurrent.total * 100
            : 0
        : undefined;
    const expiringAutorenewOffPercentageCurrentPrevious = expiringCurrentPrevious
        ? expiringCurrentPrevious.total > 0
            ? (expiringCurrentPrevious.autorenewDisabled || 0) / expiringCurrentPrevious.total * 100
            : 0
        : undefined;

    // Selected Account Stats
    const selectedAccountStatsCurrentPeriod = periodStats?.currentPeriod?.portfolioAccountStats?.stats?.[selectedAccountId];
    const selectedAccountStatsPreviousPeriod = periodStats?.previousPeriod?.portfolioAccountStats?.stats?.[selectedAccountId];

    // Calculate daily average domains renewed
    const accountDailyDomainsRenewedCurrent = selectedAccountStatsCurrentPeriod
        ? selectedAccountStatsCurrentPeriod.reduce((sum, day) => sum + day.totalDomainsRenewed, 0) / selectedAccountStatsCurrentPeriod.length
        : undefined;
    const accountDailyDomainsRenewedPrevious = selectedAccountStatsPreviousPeriod
        ? selectedAccountStatsPreviousPeriod.reduce((sum, day) => sum + day.totalDomainsRenewed, 0) / selectedAccountStatsPreviousPeriod.length
        : undefined;

    // Calculate monthly domains renewed (total for period)
    const accountMonthlyDomainsRenewedCurrent = selectedAccountStatsCurrentPeriod
        ? selectedAccountStatsCurrentPeriod.reduce((sum, day) => sum + day.totalDomainsRenewed, 0)
        : undefined;
    const accountMonthlyDomainsRenewedPrevious = selectedAccountStatsPreviousPeriod
        ? selectedAccountStatsPreviousPeriod.reduce((sum, day) => sum + day.totalDomainsRenewed, 0)
        : undefined;

    // Calculate total renewal fees
    const accountRenewalFeesCurrentPeriod = selectedAccountStatsCurrentPeriod
        ? selectedAccountStatsCurrentPeriod.reduce((sum, day) => sum + day.totalRenewalFeesPaid, 0)
        : undefined;
    const accountRenewalFeesPreviousPeriod = selectedAccountStatsPreviousPeriod
        ? selectedAccountStatsPreviousPeriod.reduce((sum, day) => sum + day.totalRenewalFeesPaid, 0)
        : undefined;

    // Calculate autorenew toggles
    const accountAutorenewEnabledCurrentPeriod = selectedAccountStatsCurrentPeriod
        ? selectedAccountStatsCurrentPeriod.reduce((sum, day) => sum + day.totalAutorenewEnabled, 0)
        : undefined;
    const accountAutorenewEnabledPreviousPeriod = selectedAccountStatsPreviousPeriod
        ? selectedAccountStatsPreviousPeriod.reduce((sum, day) => sum + day.totalAutorenewEnabled, 0)
        : undefined;

    const accountAutorenewDisabledCurrentPeriod = selectedAccountStatsCurrentPeriod
        ? selectedAccountStatsCurrentPeriod.reduce((sum, day) => sum + day.totalAutorenewDisabled, 0)
        : undefined;
    const accountAutorenewDisabledPreviousPeriod = selectedAccountStatsPreviousPeriod
        ? selectedAccountStatsPreviousPeriod.reduce((sum, day) => sum + day.totalAutorenewDisabled, 0)
        : undefined;

    // Calculate system autorenew toggles
    const accountSystemAutorenewEnabledCurrentPeriod = selectedAccountStatsCurrentPeriod
        ? selectedAccountStatsCurrentPeriod.reduce((sum, day) => sum + (day.totalDomainsSystemAutorenewEnabled || 0), 0)
        : undefined;
    const accountSystemAutorenewEnabledPreviousPeriod = selectedAccountStatsPreviousPeriod
        ? selectedAccountStatsPreviousPeriod.reduce((sum, day) => sum + (day.totalDomainsSystemAutorenewEnabled || 0), 0)
        : undefined;

    const accountSystemAutorenewDisabledCurrentPeriod = selectedAccountStatsCurrentPeriod
        ? selectedAccountStatsCurrentPeriod.reduce((sum, day) => sum + (day.totalDomainsSystemAutorenewDisabled || 0), 0)
        : undefined;
    const accountSystemAutorenewDisabledPreviousPeriod = selectedAccountStatsPreviousPeriod
        ? selectedAccountStatsPreviousPeriod.reduce((sum, day) => sum + (day.totalDomainsSystemAutorenewDisabled || 0), 0)
        : undefined;

    // Get all available account IDs from the stats
    const availableAccountIds = useMemo(() => {
        const accountIds = new Set<string>();

        // Collect account IDs from all stat sources
        if (periodStats?.currentPeriod?.portfolioAccountStats?.stats) {
            Object.keys(periodStats.currentPeriod.portfolioAccountStats.stats).forEach(id => accountIds.add(id));
        }
        if (periodStats?.currentPeriod?.autorenewStats?.stats) {
            Object.keys(periodStats.currentPeriod.autorenewStats.stats).forEach(id => accountIds.add(id));
        }
        if (periodStats?.currentPeriod?.expiringThisMonthStats?.stats) {
            Object.keys(periodStats.currentPeriod.expiringThisMonthStats.stats).forEach(id => accountIds.add(id));
        }

        return Array.from(accountIds).sort();
    }, [periodStats]);

    // Set default account if selected one is not available
    useEffect(() => {
        if (availableAccountIds.length > 0 && !availableAccountIds.includes(selectedAccountId)) {
            setSelectedAccountId(availableAccountIds[0]);
        }
    }, [availableAccountIds, selectedAccountId]);

    const sortedPortfolioMetricsStats = stats ? [...stats?.portfolioMetricsStatsDaily] : [];
    sortedPortfolioMetricsStats.sort((a,b) => {
        return b.date.getTime() - a.date.getTime();
    });

    const sortedPickingMetricsStats = stats ? [...stats?.pickingMetricsStatsDaily] : [];
    sortedPickingMetricsStats.sort((a,b) => {
        return b.date.getTime() - a.date.getTime();
    });

    const salesRevenue = stats?.listingsStatsFull || [];

    var salesVenues: { [key: string]: boolean } = {};
    stats?.listingsStatsFull.forEach(s => {
        s.stats.forEach(s => salesVenues[s.venue] = true);
    });

    const domainsStatsToday = getDomainsStats(getCurrentDayLocal());
    const domainsStatsYesterday = getDomainsStats(moment().subtract(1, 'day'));
    const domainStatsDailyAvgCurrentPeriod = periodStats ? getDomainStatsDailyAvgPeriod(periodStats?.currentPeriod) : undefined;
    const domainStatsDailyAvgPreviousPeriod = periodStats ? getDomainStatsDailyAvgPeriod(periodStats?.previousPeriod) : undefined;
    const domainStatsMTD = getDomainStatsMTD();
    const totalParkingRevenue = (stats?.parkingRevenueSummary?.reduce((acc, val) => val.revenue + acc, 0.0)) || 0.0;

    const mostRecentParkingData = stats?.parkingRevenueMTD?.reduce((acc, val) => {
        return val.date.getTime() > acc ? val.date.getTime() : acc;
    }, 0) || undefined;

    const { dateRange, setDateRange, selectedQuickRange, setSelectedQuickRange, previousPeriodRange } = useDateRangeFilter('this-month');

    const topSalesContent = topSalesTab === 'normal'
        ? (stats?.listingsTopSales ? (
            <div className="flex flex-col gap-1">
                {stats.listingsTopSales.slice(0, 10).map((l: ListingActivity, idx: number) => (
                    <div key={'normal-' + l.domain + '-' + idx} className="text-sm leading-snug">
                        <Link to={`/sales/activity?domain=${encodeURIComponent(l.domain)}`} className="text-accent hover:opacity-80 font-medium">{l.domain}</Link>
                        <span className="text-text font-mono text-sm ml-1.5">{formatCurrency(JSON.parse(l.data).amount)}</span>
                        <span className="text-text-3 text-sm ml-1">{moment(l.createdAt).format('M/D')}</span>
                    </div>
                ))}
            </div>
        ) : <span className="text-text-3 text-sm">Loading...</span>)
        : (stats?.listingsTopSalesLTO ? (
            <div className="flex flex-col gap-1">
                {stats.listingsTopSalesLTO.slice(0, 10).map((l: ListingActivity, idx: number) => (
                    <div key={'lto-' + l.domain + '-' + idx} className="text-sm leading-snug">
                        <Link to={`/sales/activity?domain=${encodeURIComponent(l.domain)}`} className="text-accent hover:opacity-80 font-medium">{l.domain}</Link>
                        <span className="text-text font-mono text-sm ml-1.5">{formatCurrency(JSON.parse(l.data).price)}</span>
                        <span className="text-text-3 text-sm ml-1">{moment(l.createdAt).format('M/D')}</span>
                    </div>
                ))}
            </div>
        ) : <span className="text-text-3 text-sm">Loading...</span>);

    const recentPayoutsList = stats?.listingsRecentPayouts ? (
        <div className="flex flex-col gap-1">
            {stats.listingsRecentPayouts.slice(0, 10).map((l: ListingActivity, idx: number) => {
                const data = JSON.parse(l.data);
                const isLTO = data.type === 'installment';
                return (
                    <div key={l.domain + '-' + idx} className="text-sm leading-snug">
                        <Link to={`/sales/activity?domain=${encodeURIComponent(l.domain)}`} className="text-accent hover:opacity-80 font-medium">{l.domain}</Link>
                        <span className="text-text font-mono text-sm ml-1.5">{formatCurrency(data.amount)}</span>
                        <span className="text-text-3 text-sm ml-1">{moment(l.createdAt).format('M/D')}</span>
                        {isLTO && <span className="text-text-3 text-2xs ml-1">(LTO)</span>}
                    </div>
                );
            })}
        </div>
    ) : <span className="text-text-3 text-sm">Loading...</span>;

    useEffect(() => {
        loadStats();
    }, [dateRange]);

    const dateLabel = selectedQuickRange
        ? selectedQuickRange.replace(/-/g, ' ')
        : `${dateRange[0].format('MMM D')} – ${dateRange[1].format('MMM D')}`;

    return (
        <>
            {contextHolder}
            <PageHead
                actions={
                    <DateRangePopover
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        selectedQuickRange={selectedQuickRange}
                        setSelectedQuickRange={setSelectedQuickRange}
                        loading={!periodStats}
                    />
                }
            />
            <PageBody>
                <Skeleton loading={!periodStats}>
                    {/* Row 1: Key Metrics | Sales Revenue | Top Sales | Recent Payouts */}
                    <div className="dashboard-row-1">
                        <DashboardCard title="Key Metrics">
                            <div className="grid grid-cols-3 gap-2">
                                <GrowthStatistic previousTitle="Previous" title="Portfolio Domains" percentage previousValue={periodStats?.previousPeriod?.totalDomainsPeriod} currentValue={periodStats?.currentPeriod?.totalDomainsPeriod} />
                                <GrowthStatistic previousTitle="Previous" title="Renewed (Daily Avg)" percentage previousValue={domainStatsDailyAvgPreviousPeriod?.additions?.total} currentValue={domainStatsDailyAvgCurrentPeriod?.additions?.total || 0} />
                                <GrowthStatistic previousTitle="Yesterday" title="Renewed (Today)" percentage previousValue={domainsStatsYesterday?.additions?.total} currentValue={domainsStatsToday?.additions?.total || 0} />
                                <GrowthStatistic unit="$" previousTitle="Previous" title="Parking Revenue" percentage previousValue={periodStats?.previousPeriod.parkingRevenueDailyAvgPeriod} currentValue={periodStats?.currentPeriod.parkingRevenueDailyAvgPeriod || undefined} />
                                <GrowthStatistic unit="$" previousTitle="Previous" title="Sales Revenue" percentage previousValue={salesRevenuePreviousPeriod} currentValue={salesRevenueCurrentPeriod} />
                                <GrowthStatistic unit="$" previousTitle="Previous" title="Recurring Revenue" percentage previousValue={salesRecurringRevenuePreviousPeriod} currentValue={salesRecurringRevenueCurrentPeriod} />
                                <GrowthStatistic previousTitle="Previous" title="Total Sales" percentage previousValue={salesPreviousPeriod} currentValue={salesCurrentPeriod} />
                                <GrowthStatistic unit="$" previousTitle="Previous" title="NC Auction" percentage previousValue={ncAftermarketAuctionRevenueLastPeriod} currentValue={ncAftermarketAuctionRevenueThisPeriod} />
                                <GrowthStatistic unit="$" previousTitle="Previous" title="NC Portfolio" percentage previousValue={ncAftermarketAuctionRevenuePortfolioPreviousPeriod} currentValue={ncAftermarketAuctionRevenuePortfolioThisPeriod} />
                                {/* TODO: unhide once LTO reconciliation is verified */}
                                {/* <GrowthStatistic unit="$" previousTitle="" title="LTO Outstanding" titleTooltip="Total remaining installment payments owed across all active LTOs" percentage={false} currentValue={stats?.ltoInstallmentsSummary?.totalRemainingAmount} /> */}
                            </div>
                        </DashboardCard>
                        <DashboardCard title="Sales Revenue">
                            <SalesRevenueSummary stats={salesRevenue} />
                        </DashboardCard>
                        <DashboardCard title={
                            <div className="flex items-center w-full">
                                <h3 className="text-base font-semibold text-text m-0">Top Sales</h3>
                                <div className="flex gap-0.5 ml-auto">
                                    {(['normal', 'lto'] as const).map((key) => (
                                        <button key={key} type="button" onClick={() => setTopSalesTab(key)}
                                            className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
                                                topSalesTab === key ? 'bg-text text-panel' : 'text-text-3 hover:text-text'
                                            }`}
                                        >{key === 'normal' ? 'Regular' : 'LTO'}</button>
                                    ))}
                                </div>
                            </div>
                        }>
                            {topSalesContent}
                        </DashboardCard>
                        {/* Recent Payouts: hidden on small, shown inline on 1600px+ */}
                        <DashboardCard className="dashboard-row-1-payouts" title="Recent Payouts">
                            {recentPayoutsList}
                        </DashboardCard>
                    </div>

                    {/* Row 1b (small screens): Recent Payouts + Parking side-by-side.
                         Hidden at 1600px+ where they live in row-1 / row-2. */}
                    <div className="dashboard-row-1b">
                        <DashboardCard title="Recent Payouts">
                            {recentPayoutsList}
                        </DashboardCard>
                        <DashboardCard title={
                            <div className="flex items-center gap-2 w-full">
                                <h3 className="text-base font-semibold text-text m-0">Parking {formatCurrencyCents(totalParkingRevenue)}</h3>
                                <span className="text-text-3 font-mono text-[10px]">
                                    updated {mostRecentParkingData ? moment(mostRecentParkingData).utc(false).format('MMM D') : 'never'}
                                </span>
                            </div>
                        }>
                            <ParkingRevenueSummary stats={stats?.parkingRevenueSummary || []} parkingRevenue={stats?.parkingRevenue || []} />
                        </DashboardCard>
                    </div>

                    {/* Row 2: Parking (large only) + Account Stats + Revenue by Rule */}
                    <div className="dashboard-row-2">
                        <DashboardCard className="dashboard-row-2-parking" title={
                            <div className="flex items-center gap-2 w-full">
                                <h3 className="text-base font-semibold text-text m-0">Parking {formatCurrencyCents(totalParkingRevenue)}</h3>
                                <span className="text-text-3 font-mono text-[10px]">
                                    updated {mostRecentParkingData ? moment(mostRecentParkingData).utc(false).format('MMM D') : 'never'}
                                </span>
                            </div>
                        }>
                            <ParkingRevenueSummary stats={stats?.parkingRevenueSummary || []} parkingRevenue={stats?.parkingRevenue || []} />
                        </DashboardCard>
                        <DashboardCard title={
                            <div className="flex items-center gap-2 w-full">
                                <h3 className="text-base font-semibold text-text m-0">Account Stats</h3>
                                <Select value={selectedAccountId} onChange={setSelectedAccountId} style={{ width: 160 }} options={availableAccountIds.map(id => ({ label: id, value: id }))} size="small" />
                            </div>
                        }>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                                <GrowthStatistic previousTitle="Previous" title="Daily Renewed" titleTooltip="Daily Domains Renewed (Avg)" percentage previousValue={accountDailyDomainsRenewedPrevious} currentValue={accountDailyDomainsRenewedCurrent} />
                                <GrowthStatistic previousTitle="Previous" title="Monthly Renewed" titleTooltip="Monthly Domains Renewed" percentage previousValue={accountMonthlyDomainsRenewedPrevious} currentValue={accountMonthlyDomainsRenewedCurrent} />
                                <GrowthStatistic unit="$" previousTitle="Previous" title="Renewal Fees" percentage previousValue={accountRenewalFeesPreviousPeriod} currentValue={accountRenewalFeesCurrentPeriod} />
                                <GrowthStatistic previousTitle="Previous" title="AR On" titleTooltip="Autorenew enabled count" percentage previousValue={accountAutorenewEnabledPreviousPeriod} currentValue={accountAutorenewEnabledCurrentPeriod} />
                                <GrowthStatistic previousTitle="Previous" title="AR Off" titleTooltip="Autorenew disabled count" percentage previousValue={accountAutorenewDisabledPreviousPeriod} currentValue={accountAutorenewDisabledCurrentPeriod} />
                                <GrowthStatistic previousTitle="Previous" title="AR Auto On" titleTooltip="Autorenew system auto-enabled" percentage previousValue={accountSystemAutorenewEnabledPreviousPeriod} currentValue={accountSystemAutorenewEnabledCurrentPeriod} onClick={() => { setAuditModalConfig({ eventTypes: ['autoRenewEnabled'], title: 'Auto Enabled' }); setAuditModalVisible(true); }} />
                                <GrowthStatistic previousTitle="Previous" title="AR Auto Off" titleTooltip="Autorenew system auto-disabled" percentage previousValue={accountSystemAutorenewDisabledPreviousPeriod} currentValue={accountSystemAutorenewDisabledCurrentPeriod} onClick={() => { setAuditModalConfig({ eventTypes: ['autoRenewDisabled'], title: 'Auto Disabled' }); setAuditModalVisible(true); }} />
                                <GrowthStatistic previousTitle="Previous" title="AR Disabled" titleTooltip="Total Autorenew disabled" percentage previousValue={autorenewDisabledPreviousPeriod} currentValue={autorenewDisabledCurrentPeriod} />
                                <GrowthStatistic previousTitle="Previous" title="Domains Expiring (Start)" titleTooltip="Expiring at period start" percentage previousValue={expiringStartOfMonthPrevious?.total} currentValue={expiringStartOfMonthCurrent?.total} />
                                <GrowthStatistic suffix="%" previousTitle="Previous" title="Exp AR Off% (SOM)" titleTooltip="% Autorenew off at start of month" percentage previousValue={expiringAutorenewOffPercentageStartPrevious} currentValue={expiringAutorenewOffPercentageStartCurrent} />
                                <GrowthStatistic previousTitle="Previous" title="Domains Expiring (Now)" titleTooltip="Expiring currently" percentage previousValue={expiringCurrentPrevious?.total} currentValue={expiringCurrentCurrent?.total} />
                                <GrowthStatistic suffix="%" previousTitle="Previous" title="Exp AR Off% (Now)" titleTooltip="% Autorenew off current" percentage previousValue={expiringAutorenewOffPercentageCurrentPrevious} currentValue={expiringAutorenewOffPercentageCurrentCurrent} />
                            </div>
                        </DashboardCard>
                        <DashboardCard title="Revenue by Rule">
                            <RevenueRuleBreakdown autopickingRevenue={stats?.autoPickingRevenue} />
                        </DashboardCard>
                    </div>

                    {/* Row 3: Renewal Stats | Metrics Stats */}
                    <div className="dashboard-row-3">
                        <DashboardCard title="Existing Method"><RenewalMethodStats stats={domainsStatsToday?.existing} statsMtd={domainStatsMTD?.existing} /></DashboardCard>
                        <DashboardCard title="Existing Pick Types"><RenewalStats stats={domainsStatsToday?.existing} statsMtd={domainStatsMTD?.existing} /></DashboardCard>
                        <DashboardCard title="Additions Method"><RenewalMethodStats hideAutoRenew stats={domainsStatsToday?.additions} statsMtd={domainStatsMTD?.additions} /></DashboardCard>
                        <DashboardCard title="Additions Pick Types"><RenewalStats hideAutoRenew stats={domainsStatsToday?.additions} statsMtd={domainStatsMTD?.additions} /></DashboardCard>
                        <DashboardCard title="Picking Metrics"><MetricsStats metricsStats={sortedPickingMetricsStats[0]} excludeParkingRevenue /></DashboardCard>
                        <DashboardCard title="Portfolio Metrics"><MetricsStats metricsStats={sortedPortfolioMetricsStats[0]} /></DashboardCard>
                    </div>

                    {/* Charts */}
                    <div className="dashboard-charts">
                        <DashboardCard title="Parking Revenue">
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={stats?.parkingRevenue.map(d => {
                                    const val: any = { name: moment(d.date).tz('UTC').format('M/D') };
                                    d.stats.forEach(s => { val[s.provider] = s.revenue / 100; });
                                    return val;
                                })} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        {stats?.parkingProviders.map((_, x) => (
                                            <linearGradient key={x} id={`park-rev-${x}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0.15} />
                                                <stop offset="100%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                    <XAxis dataKey="name" tick={axisTickStyle} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} tickFormatter={(v) => '$' + v.toLocaleString()} width={60} />
                                    <RechartsTooltip content={<ChartTooltipContent formatter={(v: number) => '$' + v.toLocaleString()} />} />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
                                    {stats?.parkingProviders.map((provider, x) => (
                                        <Area key={provider} type="monotone" dataKey={provider} stroke={chartColors[x % chartColors.length]} fill={`url(#park-rev-${x})`} strokeWidth={2} dot={false} activeDot={{ r: 3, strokeWidth: 0 }} />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Parking Visitors">
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={stats?.parkingRevenue.map(d => {
                                    const val: any = { name: moment(d.date).tz('UTC').format('M/D') };
                                    d.stats.forEach(s => { val[s.provider] = s.visitors; });
                                    return val;
                                })} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        {stats?.parkingProviders.map((_, x) => (
                                            <linearGradient key={x} id={`park-vis-${x}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0.15} />
                                                <stop offset="100%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                    <XAxis dataKey="name" tick={axisTickStyle} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} width={50} />
                                    <RechartsTooltip content={<ChartTooltipContent />} />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
                                    {stats?.parkingProviders.map((provider, x) => (
                                        <Area key={provider} type="monotone" dataKey={provider} stroke={chartColors[x % chartColors.length]} fill={`url(#park-vis-${x})`} strokeWidth={2} dot={false} activeDot={{ r: 3, strokeWidth: 0 }} />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Renewals Per Day">
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={stats?.portfolioStatsDaily.map(d => ({
                                    name: moment(d.date).tz('UTC').format('M/D'),
                                    Renewals: (d.stats?.existing?.total || 0) + (d.stats?.additions?.total || 0),
                                }))} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="renewals-fill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="rgb(var(--color-accent))" stopOpacity={0.2} />
                                            <stop offset="100%" stopColor="rgb(var(--color-accent))" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                    <XAxis dataKey="name" tick={axisTickStyle} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} width={50} />
                                    <RechartsTooltip content={<ChartTooltipContent />} />
                                    <Area type="monotone" dataKey="Renewals" stroke="rgb(var(--color-accent))" fill="url(#renewals-fill)" strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: 'rgb(var(--color-accent))' }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Sales Per Day">
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={stats?.listingsStatsFull.map(d => {
                                    const val: any = { name: moment(d.date).tz('UTC').format('M/D') };
                                    for (const stat of d.stats) { val[stat.venue] = stat.numSales; }
                                    return val;
                                })} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        {Object.keys(salesVenues).map((_, x) => (
                                            <linearGradient key={x} id={`sales-day-${x}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0.12} />
                                                <stop offset="100%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                    <XAxis dataKey="name" tick={axisTickStyle} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} width={40} />
                                    <RechartsTooltip content={<ChartTooltipContent />} />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
                                    {Object.keys(salesVenues).map((venue, x) => (
                                        <Area key={venue} type="monotone" dataKey={venue} stroke={chartColors[x % chartColors.length]} fill={`url(#sales-day-${x})`} strokeWidth={2} dot={false} activeDot={{ r: 3, strokeWidth: 0 }} />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Sales Revenue Per Day">
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={stats?.listingsStatsFull.map(d => {
                                    const val: any = { name: moment(d.date).tz('UTC').format('M/D') };
                                    for (const stat of d.stats) { val[stat.venue] = stat.revenueUSD; }
                                    return val;
                                })} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        {Object.keys(salesVenues).map((_, x) => (
                                            <linearGradient key={x} id={`sales-rev-${x}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0.12} />
                                                <stop offset="100%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                    <XAxis dataKey="name" tick={axisTickStyle} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} tickFormatter={(v) => '$' + v.toLocaleString()} width={60} />
                                    <RechartsTooltip content={<ChartTooltipContent formatter={(v: number) => '$' + v.toLocaleString()} />} />
                                    <Legend iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
                                    {Object.keys(salesVenues).map((venue, x) => (
                                        <Area key={venue} type="monotone" dataKey={venue} stroke={chartColors[x % chartColors.length]} fill={`url(#sales-rev-${x})`} strokeWidth={2} dot={false} activeDot={{ r: 3, strokeWidth: 0 }} />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </DashboardCard>
                    </div>
                </Skeleton>
            </PageBody>

            {auditModalConfig && (
                <AuditEventsModal
                    visible={auditModalVisible}
                    onClose={() => setAuditModalVisible(false)}
                    startDate={dayjs.utc(dateRange[0].format('YYYY-MM-DD')).startOf('day').toDate()}
                    endDate={dayjs.utc(dateRange[1].format('YYYY-MM-DD')).add(1, 'day').startOf('day').toDate()}
                    eventTypes={auditModalConfig.eventTypes}
                    account={selectedAccountId}
                    title={auditModalConfig.title}
                />
            )}
        </>
    );
};

interface DateRangePopoverProps {
    dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
    setDateRange: React.Dispatch<React.SetStateAction<[dayjs.Dayjs, dayjs.Dayjs] | null>>;
    selectedQuickRange: string | null;
    setSelectedQuickRange: React.Dispatch<React.SetStateAction<string | null>>;
    loading?: boolean;
}

function DateRangePopover({
    dateRange,
    setDateRange,
    selectedQuickRange,
    setSelectedQuickRange,
    loading,
}: DateRangePopoverProps) {
    const [open, setOpen] = useState(false);
    const label = selectedQuickRange
        ? defaultQuickRanges.find((r) => r.value === selectedQuickRange)?.label ?? selectedQuickRange
        : dateRange
        ? `${dateRange[0].format('MMM D')} – ${dateRange[1].format('MMM D')}`
        : '—';

    function applyCustomRange(dates: any) {
        if (!dates || !dates[0] || !dates[1]) return;
        setDateRange([dates[0], dates[1]]);
        setSelectedQuickRange(null);
    }

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            placement="bottomRight"
            content={
                <div className="flex w-[440px]">
                    {/* Quick ranges */}
                    <div className="flex flex-col w-[160px] py-1 border-r border-border">
                        <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-text-3 font-semibold">
                            Quick range
                        </div>
                        {defaultQuickRanges.map((r) => {
                            const active = selectedQuickRange === r.value;
                            return (
                                <button
                                    key={r.value}
                                    type="button"
                                    onClick={() => {
                                        setDateRange(r.range);
                                        setSelectedQuickRange(r.value);
                                        setOpen(false);
                                    }}
                                    className={`text-left mx-1 px-2 py-1.5 text-[12.5px] rounded hover:bg-bg-elevated ${
                                        active ? 'text-accent font-semibold bg-accent-soft' : 'text-text-2'
                                    }`}
                                >
                                    {r.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Custom range */}
                    <div className="flex-1 py-1 px-3 flex flex-col gap-2">
                        <div className="pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-text-3 font-semibold">
                            Custom range
                        </div>
                        <DatePicker.RangePicker
                            value={dateRange}
                            onChange={applyCustomRange}
                            allowClear={false}
                            size="small"
                            className="w-full"
                        />
                        {dateRange && (
                            <div className="text-[11px] text-text-3 font-mono leading-snug mt-1">
                                <div>start <span className="text-text-2">{dateRange[0].format('MMM D, YYYY')}</span></div>
                                <div>end <span className="text-text-2">{dateRange[1].format('MMM D, YYYY')}</span></div>
                                <div>days <span className="text-text-2">{dateRange[1].diff(dateRange[0], 'day') + 1}</span></div>
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <span className="inline-flex items-center gap-2">
                {loading && (
                    <span className="text-[10.5px] font-mono text-text-3">loading…</span>
                )}
                <FilterPill label="Date" value={label} active={!!selectedQuickRange || !!dateRange} />
            </span>
        </Popover>
    );
}

export default Home;
